import * as React from "react"
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Icon,
  cn,
} from "@nexus/ui"
import { useTranslation } from "react-i18next"
import { canAnimate } from "@/lib/motion"
import { StoryboardArtDirectionNote } from "../ScriptStoryboard/StoryboardArtDirectionNote"
import type { ScriptScene, StoryboardPanel } from "@/features/opportunities/api/opportunities.api"

/** Words per second used to estimate how long a spoken line takes to deliver. */
const WORDS_PER_SECOND = 2.5
/** A frame always holds long enough to read, and never outstays its welcome. */
const MIN_DWELL_MS = 1_800
const MAX_DWELL_MS = 5_000
/** Used when a panel has no matching scene to time against. */
const DEFAULT_DWELL_MS = 3_000
/**
 * Playback waits for every frame to decode, but not forever. A frame whose signed
 * URL has expired, or whose fetch stalls, would otherwise strand the player on a
 * spinner; after this it plays with whatever arrived.
 */
const PRELOAD_TIMEOUT_MS = 8_000
/**
 * Longest caption shown at once. A spoken line runs to several sentences, which as
 * one block is a paragraph rather than a caption; broadcast subtitles cap a cue at
 * roughly two lines, and two lines of this column is about this many characters.
 */
const MAX_CUE_CHARS = 80
/** Quote characters the model wraps spoken lines in. Subtitles never show them. */
const QUOTE_CHARS = "\"'\u201C\u201D\u2018\u2019"
/** Playback position, 0–1, published on the scrubber for its parts to read in CSS. */
const PROGRESS_VAR = "--animatic-progress"
/** Playback rates offered by the speed menu, in the order listed. */
const SPEEDS: readonly number[] = [0.5, 0.75, 1, 1.5, 2]
/**
 * Width of the expanded column: the 9:16 frame sized to fit the viewport. The
 * modal title shares it so a long hook truncates at the player's edge instead
 * of widening the dialog or wrapping onto a second line.
 */
const EXPANDED_COLUMN_WIDTH = "w-[min(80vw,calc(72svh*9/16))]"

interface AnimaticPlayerProps {
  panels: StoryboardPanel[]
  /** Scenes supply the spoken line each frame is timed against. */
  scenes: ScriptScene[]
  /** Names the script in the expanded modal, which can be opened straight from a link. */
  title?: string | null
  /** Controlled modal state — omit to let the player own expansion locally. */
  expanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
}

interface Frame {
  panel: StoryboardPanel
  /** How long this frame holds before handing over to the next. */
  dwellMs: number
  /**
   * What is heard over this frame — its scene's line, or the panel's caption when
   * there is none — broken into caption-sized pieces and timed across the dwell.
   */
  cues: string[]
}

/**
 * Drop a speaker attribution — "Narrator:", "VO (wry):", "Mom (off-screen):".
 * A subtitle names nobody: the frame already shows who is speaking.
 *
 * Only an attribution goes, never a line that merely contains a colon, so what
 * precedes the colon has to read as a name once its delivery note is set aside:
 * at most three words, every one of them capitalised, no sentence punctuation.
 * "One thing is true: denim should move" therefore survives intact.
 */
function stripSpeakerLabel(line: string): string {
  const match = /^([^:]{1,48}):\s*/.exec(line)
  if (!match?.[1]) return line
  const name = match[1].replace(/\([^)]*\)/g, " ").trim()
  const words = name ? name.split(/\s+/) : []
  const isSpeakerName =
    words.length > 0 &&
    words.length <= 3 &&
    !/[.!?,;]/.test(name) &&
    words.every((word) => /^[A-Z0-9]/.test(word))
  return isSpeakerName ? line.slice(match[0].length) : line
}

/**
 * Turn a generated script line into something a viewer would accept as a
 * subtitle: no speaker label, no stage direction, no quotation marks around
 * dialogue that is already obviously dialogue.
 */
function humanizeLine(raw: string): string {
  let line = raw.trim()
  line = stripSpeakerLabel(line)
  // A leading direction or sound cue — "(beat)", "[SFX: whoosh]" — is production
  // shorthand, not something anyone hears.
  line = line.replace(/^(\([^)]*\)|\[[^\]]*\])\s*/g, "").trim()
  line = line.replace(new RegExp(`^[${QUOTE_CHARS}]+|[${QUOTE_CHARS}]+$`, "g"), "").trim()
  return line
}

/**
 * Final pass on a single cue, applied after splitting because a split can leave a
 * quote mark stranded at an edge. The closing full stop goes too: on screen the
 * cue's disappearance ends the sentence, so the dot is only clutter — while "?"
 * and "!" carry tone and stay.
 */
function polishCue(cue: string): string {
  let out = cue.replace(new RegExp(`^[${QUOTE_CHARS}]+|[${QUOTE_CHARS}]+$`, "g"), "").trim()
  if (out.endsWith(".") && !out.endsWith("..")) out = out.slice(0, -1).trim()
  return out
}

/**
 * Break a spoken line into caption cues, preferring to hand over at the end of a
 * sentence and falling back to packing words up to the character budget.
 */
function splitIntoCues(line: string): string[] {
  const cues: string[] = []
  let cue = ""

  for (const word of line.split(/\s+/).filter(Boolean)) {
    const candidate = cue ? `${cue} ${word}` : word
    if (cue && candidate.length > MAX_CUE_CHARS) {
      cues.push(cue)
      cue = word
    } else {
      cue = candidate
    }
    // Break on a sentence ending, but not so early that a cue holds two words.
    if (/[.!?]['")\]]?$/.test(word) && cue.length > MAX_CUE_CHARS / 2) {
      cues.push(cue)
      cue = ""
    }
  }
  if (cue) cues.push(cue)

  return (cues.length > 0 ? cues : [line]).map(polishCue).filter(Boolean)
}

/** Which cue of a frame is on screen at a given point into its dwell. */
function cueIndexAt(frame: Frame | undefined, offsetMs: number): number {
  if (!frame || frame.cues.length <= 1) return 0
  const perCueMs = frame.dwellMs / frame.cues.length
  return Math.min(frame.cues.length - 1, Math.max(0, Math.floor(offsetMs / perCueMs)))
}

/** Which frame covers a point on the overall timeline. */
function frameIndexAt(starts: readonly number[], targetMs: number): number {
  for (let i = starts.length - 1; i >= 0; i -= 1) {
    if (targetMs >= (starts[i] ?? 0)) return i
  }
  return 0
}

/** A position in the cut, as a viewer reads it off a player: `0:07`. */
function formatClock(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1_000))
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`
}

/**
 * Pair each frame with its pacing and the part of the spoken line it carries.
 *
 * A scene is covered by several frames (its action's start and end), so the scene's
 * spoken duration is divided between them — otherwise a two-frame scene would hold
 * for twice as long as its line takes to say. Its caption cues are dealt out the
 * same way, so the subtitle advances through the scene instead of repeating the
 * whole line on every frame. Frames name their scene via `sceneIndex`; frames
 * generated before that existed fall back to matching by position, and a frame with
 * no scene to time against falls back to a fixed dwell.
 */
function planFrames(panels: StoryboardPanel[], scenes: ScriptScene[]): Frame[] {
  const framesPerScene = new Map<number, number>()
  for (const panel of panels) {
    if (panel.sceneIndex === undefined) continue
    framesPerScene.set(panel.sceneIndex, (framesPerScene.get(panel.sceneIndex) ?? 0) + 1)
  }

  const seenPerScene = new Map<number, number>()

  return panels.map((panel, i) => {
    const scene =
      panel.sceneIndex === undefined
        ? scenes[i]
        : scenes.find((s) => s.scene_number === panel.sceneIndex)
    const spokenLine = humanizeLine(scene?.hearing ?? "")
    const line = spokenLine || humanizeLine(panel.caption)
    const words = spokenLine.split(/\s+/).filter(Boolean).length
    const share = panel.sceneIndex === undefined ? 1 : (framesPerScene.get(panel.sceneIndex) ?? 1)
    const spoken = ((words / WORDS_PER_SECOND) * 1_000) / share

    const ordinal = panel.sceneIndex === undefined ? 0 : (seenPerScene.get(panel.sceneIndex) ?? 0)
    if (panel.sceneIndex !== undefined) seenPerScene.set(panel.sceneIndex, ordinal + 1)
    const allCues = splitIntoCues(line)
    const slice = allCues.slice(
      Math.round((ordinal * allCues.length) / share),
      Math.round(((ordinal + 1) * allCues.length) / share),
    )

    return {
      panel,
      dwellMs:
        words === 0
          ? DEFAULT_DWELL_MS
          : Math.min(MAX_DWELL_MS, Math.max(MIN_DWELL_MS, Math.round(spoken))),
      // Fewer cues than frames: later frames hold the last cue rather than nothing.
      cues:
        slice.length > 0
          ? slice
          : [allCues[Math.min(ordinal, allCues.length - 1)] ?? polishCue(line)],
    }
  })
}

/**
 * Plays photoreal storyboard frames as an animatic: an auto-advancing sequence
 * with a crossfade, approximating the finished ad without a video model.
 *
 * Every frame is mounted from the start and playback only crossfades opacity
 * between them, so no frame is ever fetched at the moment it is shown. Playback
 * waits for them all to decode first, because a mid-sequence stall reads as a
 * fault in the ad rather than in the network.
 *
 * Handovers follow an editor's grammar rather than one uniform fade: the two
 * frames of a scene dissolve into each other slowly, which reads as the scene's
 * motion, while a scene change hands over fast, which reads as a cut.
 *
 * Auto-advance is a chained timeout rather than an interval, because each frame
 * holds for its own duration. Under `prefers-reduced-motion` the player mounts
 * paused and never animates, so the frames become a plain manual gallery.
 */
export function AnimaticPlayer({
  panels,
  scenes,
  title,
  expanded,
  onExpandedChange,
}: AnimaticPlayerProps) {
  const { t } = useTranslation()
  const motionAllowed = React.useMemo(() => canAnimate(), [])
  const [index, setIndex] = React.useState(0)
  const [isPlaying, setIsPlaying] = React.useState(motionAllowed)
  // Playback rate. The frame clock counts in nominal media time — real elapsed time
  // is scaled by the rate as it is measured — so dwells, the timeline and the
  // scrubber's clock all stay in the same units whatever the speed.
  const [speed, setSpeed] = React.useState<number>(1)
  const [internalExpanded, setInternalExpanded] = React.useState(false)
  const isExpanded = expanded ?? internalExpanded
  const setExpanded = React.useCallback(
    (next: boolean) => {
      setInternalExpanded(next)
      onExpandedChange?.(next)
    },
    [onExpandedChange],
  )

  const frames = React.useMemo(() => planFrames(panels, scenes), [panels, scenes])
  const total = frames.length
  const current = frames[index]

  // Where each frame starts on the overall timeline, so the playhead can express
  // "frame 3, two seconds in" as one continuous position along the whole cut.
  const timeline = React.useMemo(() => {
    let acc = 0
    const starts = frames.map((frame) => {
      const start = acc
      acc += frame.dwellMs
      return start
    })
    return { starts, totalMs: acc }
  }, [frames])

  // Preload accounting. A frame that fails counts as settled: waiting on it would
  // hold the whole sequence hostage to one broken URL.
  const expected = React.useMemo(() => frames.filter((f) => f.panel.imageUrl).length, [frames])
  const [settled, setSettled] = React.useState<ReadonlySet<number>>(() => new Set())
  const markSettled = React.useCallback((panelIndex: number) => {
    setSettled((prev) => (prev.has(panelIndex) ? prev : new Set(prev).add(panelIndex)))
  }, [])

  const [hasWaitedLongEnough, setHasWaitedLongEnough] = React.useState(false)
  React.useEffect(() => {
    const timer = window.setTimeout(() => setHasWaitedLongEnough(true), PRELOAD_TIMEOUT_MS)
    return () => window.clearTimeout(timer)
  }, [])

  const isReady = settled.size >= expected || hasWaitedLongEnough
  const isCurrentLoaded =
    !current?.panel.imageUrl || settled.has(current.panel.panelIndex) || hasWaitedLongEnough

  // Which cue of the current frame is on screen. Captions step through a frame's
  // cues over its dwell, so several sentences read as subtitles instead of
  // arriving as one block of text.
  const [cueIndex, setCueIndex] = React.useState(0)

  // The frame clock: how much of the current frame's dwell has already played.
  // It lets a pause resume mid-frame, and drives the playhead between renders.
  const frameClockRef = React.useRef<{ accumulatedMs: number; playingSince: number | null }>({
    accumulatedMs: 0,
    playingSince: null,
  })
  // A seek lands part-way into a frame, so it sets the clock itself and tells this
  // effect to stand down for that one frame change. Bumping `seekNonce` re-arms the
  // advance and repaints the playhead even when the seek stayed on the same frame.
  const isSeekAppliedRef = React.useRef(false)
  const [seekNonce, setSeekNonce] = React.useState(0)
  React.useEffect(() => {
    if (isSeekAppliedRef.current) {
      isSeekAppliedRef.current = false
      return
    }
    frameClockRef.current = { accumulatedMs: 0, playingSince: null }
    setCueIndex(0)
  }, [index])

  // Chained advance: re-armed on every index change while playing. Armed with the
  // frame's *remaining* dwell, so pausing and resuming picks the frame up where it
  // stopped instead of replaying it from the start.
  React.useEffect(() => {
    if (!isPlaying || !isReady || total <= 1) return
    const clock = frameClockRef.current
    clock.playingSince = performance.now()
    const timer = window.setTimeout(
      () => {
        setIndex((i) => (i + 1) % total)
      },
      Math.max(0, (current?.dwellMs ?? DEFAULT_DWELL_MS) - clock.accumulatedMs) / speed,
    )
    return () => {
      window.clearTimeout(timer)
      if (clock.playingSince !== null) {
        clock.accumulatedMs += (performance.now() - clock.playingSince) * speed
        clock.playingSince = null
      }
    }
  }, [isPlaying, isReady, index, current?.dwellMs, total, seekNonce, speed])

  // The playhead runs on the clock, not on React renders: while playing it is
  // advanced every animation frame by writing the transform directly, so the bar
  // sweeps continuously — like a movie's timeline — without re-rendering the
  // player at frame rate. Paused (and under reduced motion) it is placed once.
  // Position is published as one CSS variable on the scrubber rather than written
  // to each moving part: the filled bar and the handle then derive their transform
  // from it in CSS, so they cannot drift out of step and neither needs the track's
  // width in pixels — a measurement that is wrong for one frame after the player
  // moves into the modal, and stale after any resize that goes unobserved.
  const trackRef = React.useRef<HTMLDivElement>(null)
  const scrubberRef = React.useRef<HTMLDivElement>(null)
  const paint = React.useCallback((fraction: number) => {
    const at = Math.min(1, Math.max(0, fraction))
    scrubberRef.current?.style.setProperty(PROGRESS_VAR, String(at))
  }, [])

  React.useEffect(() => {
    if (total <= 1) {
      paint(1)
      return
    }
    const clock = frameClockRef.current
    const start = timeline.starts[index] ?? 0
    const dwell = current?.dwellMs ?? DEFAULT_DWELL_MS
    const at = (elapsedMs: number) => (start + Math.min(elapsedMs, dwell)) / timeline.totalMs

    if (!isPlaying || !isReady || !motionAllowed) {
      paint(at(clock.accumulatedMs))
      return
    }
    let raf = window.requestAnimationFrame(function tick() {
      const elapsed =
        clock.playingSince === null
          ? clock.accumulatedMs
          : clock.accumulatedMs + (performance.now() - clock.playingSince) * speed
      paint(at(elapsed))
      raf = window.requestAnimationFrame(tick)
    })
    return () => window.cancelAnimationFrame(raf)
    // isExpanded remounts the scrubber inside the dialog, so republish the position.
  }, [
    isPlaying,
    isReady,
    index,
    motionAllowed,
    timeline,
    current?.dwellMs,
    total,
    isExpanded,
    seekNonce,
    speed,
    paint,
  ])

  const cues = current?.cues ?? []
  React.useEffect(() => {
    if (!isPlaying || !isReady || cues.length <= 1) return
    const timer = window.setInterval(
      () => setCueIndex((i) => Math.min(i + 1, cues.length - 1)),
      (current?.dwellMs ?? DEFAULT_DWELL_MS) / cues.length / speed,
    )
    return () => window.clearInterval(timer)
  }, [isPlaying, isReady, index, cues.length, current?.dwellMs, speed])

  const goTo = React.useCallback(
    (next: number) => {
      // Manual navigation takes over: stop auto-advance so the user keeps control.
      setIsPlaying(false)
      setIndex(((next % total) + total) % total)
    },
    [total],
  )

  // Scrubbing. A position on the bar is a point on the whole cut, so it resolves to
  // the frame that covers it plus how far into that frame it falls — the same pair
  // the frame clock already speaks in, which is what lets a seek land mid-frame
  // rather than snapping to a frame boundary.
  const seekToFraction = React.useCallback(
    (fraction: number) => {
      const at = Math.min(1, Math.max(0, fraction))
      const targetMs = at * timeline.totalMs
      const next = frameIndexAt(timeline.starts, targetMs)
      const offsetMs = Math.max(0, targetMs - (timeline.starts[next] ?? 0))

      frameClockRef.current = { accumulatedMs: offsetMs, playingSince: null }
      setCueIndex(cueIndexAt(frames[next], offsetMs))
      if (next !== index) {
        isSeekAppliedRef.current = true
        setIndex(next)
      }
      setSeekNonce((n) => n + 1)
      paint(at)
    },
    [frames, index, paint, timeline],
  )

  const [isScrubbing, setIsScrubbing] = React.useState(false)
  /** Where the pointer is over the bar, 0–1, driving the preview. Null when away. */
  const [hoverAt, setHoverAt] = React.useState<number | null>(null)
  /**
   * Seeking leaves the play state alone, the way every video player does: playback
   * merely holds still while the pointer is down and picks up from the landing
   * point on release, so a click on the bar never reads as a pause.
   */
  const wasPlayingBeforeScrubRef = React.useRef(false)

  const fractionFromClientX = React.useCallback((clientX: number) => {
    const box = trackRef.current?.getBoundingClientRect()
    if (!box || box.width === 0) return null
    return Math.min(1, Math.max(0, (clientX - box.left) / box.width))
  }, [])
  const handleScrubStart = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isReady || total <= 1) return
    const at = fractionFromClientX(event.clientX)
    if (at === null) return
    wasPlayingBeforeScrubRef.current = isPlaying
    setIsPlaying(false)
    setIsScrubbing(true)
    event.currentTarget.setPointerCapture?.(event.pointerId)
    seekToFraction(at)
  }
  const handleScrubMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const at = fractionFromClientX(event.clientX)
    if (at === null) return
    setHoverAt(at)
    if (isScrubbing) seekToFraction(at)
  }
  const handleScrubEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isScrubbing) return
    setIsScrubbing(false)
    if (wasPlayingBeforeScrubRef.current) setIsPlaying(true)
    event.currentTarget.releasePointerCapture?.(event.pointerId)
  }
  // Keyboard seeking works in frames, because that is what the slider reports as
  // its value and what a viewer of a ten-frame animatic actually wants to step by.
  const handleScrubKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const seeks: Record<string, number | undefined> = {
      ArrowLeft: index - 1,
      ArrowDown: index - 1,
      ArrowRight: index + 1,
      ArrowUp: index + 1,
      Home: 0,
      End: total - 1,
    }
    const next = seeks[event.key]
    if (next === undefined || !isReady) return
    event.preventDefault()
    goTo(next)
  }

  // Editing grammar. Two frames of one scene are the start and end of a single
  // movement, so blending between them long reads as that motion; a scene change
  // is an edit and hands over fast, like a cut. The ref still holds the outgoing
  // index during the render that switches frames, which is the moment the choice
  // applies; frames without a sceneIndex (generated before it existed) always cut.
  const prevIndexRef = React.useRef(index)
  const prevFrame = frames[prevIndexRef.current]
  const isSameSceneHandover =
    current?.panel.sceneIndex !== undefined && prevFrame?.panel.sceneIndex === current?.panel.sceneIndex
  React.useEffect(() => {
    prevIndexRef.current = index
  }, [index])

  if (total === 0 || !current) return null

  // What the hover preview says: the position in the cut, and the beat there.
  const hoverPreview =
    hoverAt === null
      ? null
      : {
          clock: formatClock(hoverAt * timeline.totalMs),
          label: frames[frameIndexAt(timeline.starts, hoverAt * timeline.totalMs)]?.panel.label ?? "",
        }

  // The whole player is one column the width of the frame, so the caption, the
  // progress bar and the transport read as parts of it rather than as page content.
  // Expanded, the column is sized by the viewport height so the 9:16 frame fills
  // the modal without overflowing it.
  const player = (
    <div
      className={cn("mx-auto space-y-3", isExpanded ? EXPANDED_COLUMN_WIDTH : "w-full max-w-xs")}
    >
      <div
        data-transition={isSameSceneHandover ? "dissolve" : "cut"}
        className="group/stage animatic-stage relative aspect-[9/16] w-full overflow-hidden rounded-lg border border-border/40 bg-muted"
      >
        {frames.map((frame, i) =>
          frame.panel.imageUrl ? (
            <img
              key={frame.panel.panelIndex}
              src={frame.panel.imageUrl}
              // Only the frame on screen is described; the rest are mounted purely so
              // they are already decoded when their turn comes.
              alt={
                i === index
                  ? t(
                      "opportunities.scripts.storyboard.animatic.frameAlt",
                      "Storyboard frame {{index}} of {{total}}: {{label}}",
                      { index: index + 1, total, label: frame.panel.label },
                    )
                  : ""
              }
              aria-hidden={i !== index}
              decoding="async"
              onLoad={() => markSettled(frame.panel.panelIndex)}
              onError={() => markSettled(frame.panel.panelIndex)}
              // De-sync each frame's drift: a staggered start point in the loop and a
              // cycled origin give every frame its own camera move, so a dissolve
              // blends two different motions — in sync they would blend as one image.
              // Scale only ever grows from an inset-0 cover fit, so no origin exposes
              // an edge. Reduced motion removes the animation, making the delay inert.
              style={{ animationDelay: `${i * -3.5}s` }}
              className={cn(
                "animatic-frame absolute inset-0 h-full w-full object-cover",
                ["origin-center", "origin-top", "origin-bottom"][i % 3],
                i === index ? "opacity-100" : "opacity-0",
              )}
            />
          ) : null,
        )}

        {/* A constant, gentle edge vignette unifies frames rendered in separate model
            calls under one "grade", the way a finished spot is. Decorative only. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_120%_at_50%_40%,transparent_62%,rgb(0_0_0/0.2)_100%)]"
        />

        {!current.panel.imageUrl && (
          <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-sm text-muted-foreground">
            {t("opportunities.scripts.storyboard.imageUnavailable")}
          </div>
        )}

        {current.panel.imageUrl && !isCurrentLoaded && (
          <div
            role="status"
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-muted"
          >
            <Icon name="loader" className="size-5 animate-spin text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              {t(
                "opportunities.scripts.storyboard.animatic.preparing",
                "Preparing animatic…",
              )}
            </span>
          </div>
        )}

        {/* Silent for assistive tech: the blocking status above already said the animatic
            is preparing, and a per-frame count would announce on every arrival. Sits at
            the top so it never collides with the caption's lower third. */}
        {isCurrentLoaded && !isReady && (
          <span
            aria-hidden="true"
            className="absolute right-2 top-2 flex items-center gap-1.5 rounded-full bg-background/85 px-2 py-1 text-[10px] font-medium text-muted-foreground backdrop-blur-sm"
          >
            <Icon name="loader" className="size-3 animate-spin" />
            {t("opportunities.scripts.storyboard.animatic.framesReady", "{{loaded}} of {{total}}", {
              loaded: settled.size,
              total: expected,
            })}
          </span>
        )}

        <span className="absolute left-2 top-2 rounded bg-background/80 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground backdrop-blur-sm">
          {current.panel.label}
        </span>

        {/* What is heard over this frame, as real text — the frames deliberately carry
            none, and the animatic itself is silent. Set the way a film sets subtitles:
            white type held off the footage by a shadow rather than a caption box, over
            a scrim that is always present so a cue change never makes the frame appear
            to flicker. The shadow, not the scrim alone, is what guarantees contrast
            when a frame is bright. The aria-live paragraph stays mounted; only the
            keyed span inside remounts, so each cue fades in without re-announcing the
            region itself. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex min-h-28 items-end justify-center bg-gradient-to-t from-black/85 via-black/45 to-transparent px-4 pb-4">
          <p
            aria-live="polite"
            className="text-balance text-center text-sm font-medium leading-snug text-white [text-shadow:0_1px_2px_rgb(0_0_0/0.95),0_0_10px_rgb(0_0_0/0.65)]"
          >
            <span
              key={`${index}-${cueIndex}`}
              className="block motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300 motion-safe:fill-mode-both"
            >
              {cues[Math.min(cueIndex, cues.length - 1)]}
            </span>
          </p>
        </div>

        {/* The frame is the play/pause control, as on every video player. The whole
            stage is the tap target, so touch never depends on hover; the glass badge
            is only the affordance — held on screen while paused, revealed on hover
            or keyboard focus while playing. */}
        {isReady && (
          <button
            type="button"
            className="group/toggle absolute inset-0 z-10 flex cursor-pointer items-center justify-center outline-none"
            aria-label={
              isPlaying
                ? t("opportunities.scripts.storyboard.animatic.pause", "Pause")
                : t("opportunities.scripts.storyboard.animatic.play", "Play")
            }
            onClick={() => setIsPlaying((p) => !p)}
          >
            <span
              className={cn(
                "flex size-14 items-center justify-center rounded-full bg-black/55 text-white shadow-md backdrop-blur-sm transition-opacity duration-200 motion-reduce:transition-none",
                "group-focus-visible/toggle:ring-2 group-focus-visible/toggle:ring-white/80",
                isPlaying
                  ? "opacity-0 group-hover/stage:opacity-100 group-focus-visible/toggle:opacity-100"
                  : "opacity-100",
              )}
            >
              <Icon name={isPlaying ? "pause" : "play"} color="inherit" className="size-6" />
            </span>
          </button>
        )}
      </div>

      {/* A movie-style scrubber: the playhead sweeps continuously through each
          frame's dwell instead of stepping once per frame, the ticks mark the frame
          boundaries like scene markers, and the handle can be dragged or the bar
          clicked to seek. The hit area is a full touch target even though the bar
          it contains is hairline-thin. */}
      <div
        ref={scrubberRef}
        role="slider"
        tabIndex={isReady ? 0 : -1}
        aria-label={t("opportunities.scripts.storyboard.animatic.timeline", "Playback position")}
        aria-valuemin={1}
        aria-valuemax={total}
        aria-valuenow={index + 1}
        aria-valuetext={t(
          "opportunities.scripts.storyboard.animatic.position",
          "Frame {{current}} of {{total}}",
          { current: index + 1, total },
        )}
        aria-disabled={!isReady}
        onPointerDown={handleScrubStart}
        onPointerMove={handleScrubMove}
        onPointerUp={handleScrubEnd}
        onPointerCancel={handleScrubEnd}
        onPointerLeave={() => setHoverAt(null)}
        onKeyDown={handleScrubKeyDown}
        className={cn(
          "animatic-scrubber group relative flex w-full touch-none select-none items-center outline-none",
          "min-h-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-5",
          isReady && total > 1 ? "cursor-pointer" : "cursor-default",
        )}
      >
        <div
          ref={trackRef}
          className={cn(
            "animatic-timeline relative w-full overflow-hidden rounded-full bg-primary/15 transition-[height] duration-150 ease-out motion-reduce:transition-none",
            "group-focus-visible:ring-2 group-focus-visible:ring-ring/60 group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-background",
            isScrubbing ? "h-2" : "h-1.5 group-hover:h-2",
          )}
        >
          <span
            aria-hidden="true"
            style={{ transform: `scaleX(var(${PROGRESS_VAR}, 0))` }}
            className={cn(
              "animatic-playhead absolute inset-0 origin-left rounded-full",
              isPlaying || isScrubbing ? "bg-primary" : "bg-primary/60",
            )}
          />
          {timeline.starts.slice(1).map((startMs, i) => (
            <span
              key={frames[i + 1]?.panel.panelIndex ?? startMs}
              style={{ left: `${(startMs / timeline.totalMs) * 100}%` }}
              className="absolute inset-y-0 w-px bg-background/80"
            />
          ))}
        </div>

        {/* The grabbable dot. It rides a full-width rail translated by a percentage
            of its own width, which is how it lands at the playhead without anyone
            knowing the track's width in pixels. Decorative: the slider around it
            carries the position and the keyboard handling. Hover and drag grow it by
            size rather than scale, so nothing competes with the rail's transform. */}
        <span
          aria-hidden="true"
          style={{ transform: `translateX(calc(var(${PROGRESS_VAR}, 0) * 100%))` }}
          className="animatic-handle pointer-events-none absolute inset-y-0 left-0 w-full"
        >
          <span
            className={cn(
              "absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary shadow-sm ring-2 ring-background transition-[width,height] duration-150 ease-out motion-reduce:transition-none",
              isScrubbing ? "size-4" : "size-3 group-hover:size-4 group-focus-visible:size-4",
            )}
          />
        </span>

        {/* Hover preview, as a video player gives you: where you would land, and
            which beat of the script lives there. Centred on the pointer but kept a
            handle's width inside each end, so it never spills out of the card. */}
        {hoverAt !== null && hoverPreview && (
          <span
            aria-hidden="true"
            style={{ left: `clamp(4rem, ${hoverAt * 100}%, calc(100% - 4rem))` }}
            className="animatic-preview pointer-events-none absolute bottom-full z-10 mb-2 -translate-x-1/2 whitespace-nowrap rounded-md border border-border bg-popover px-2 py-1 text-[11px] leading-none text-popover-foreground shadow-md"
          >
            <span className="font-medium tabular-nums">{hoverPreview.clock}</span>
            <span className="text-muted-foreground"> · {hoverPreview.label}</span>
          </span>
        )}
      </div>

      {/* The transport, grouped like a video player's control bar: stepping and the
          position on the left, presentation controls on the right. Play/pause lives
          on the frame itself, so the row carries no toggle of its own. */}
      <div className="flex items-center justify-between gap-1">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            iconStart="chevron-left"
            disabled={!isReady}
            className="min-h-11 min-w-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-8 [@media(hover:hover)_and_(pointer:fine)]:min-w-8"
            aria-label={t("opportunities.scripts.storyboard.animatic.previous", "Previous frame")}
            onClick={() => goTo(index - 1)}
          />
          <Button
            variant="ghost"
            size="sm"
            iconStart="chevron-right"
            disabled={!isReady}
            className="min-h-11 min-w-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-8 [@media(hover:hover)_and_(pointer:fine)]:min-w-8"
            aria-label={t("opportunities.scripts.storyboard.animatic.next", "Next frame")}
            onClick={() => goTo(index + 1)}
          />
          <span className="ml-1 text-xs tabular-nums text-muted-foreground">
            {t("opportunities.scripts.storyboard.animatic.counter", "{{current}} / {{total}}", {
              current: index + 1,
              total,
            })}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {/* Speed opens upward, the way a video player's rate menu does: every rate
              is visible and one pick away, instead of blind-cycling through five. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                disabled={!isReady}
                className="min-h-11 min-w-11 tabular-nums [@media(hover:hover)_and_(pointer:fine)]:min-h-8 [@media(hover:hover)_and_(pointer:fine)]:min-w-8"
                aria-label={t("opportunities.scripts.storyboard.animatic.speed", "Speed: {{speed}}×", {
                  speed,
                })}
              >
                {speed}×
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="center">
              <DropdownMenuLabel>
                {t("opportunities.scripts.storyboard.animatic.speedMenuLabel", "Playback speed")}
              </DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={String(speed)}
                onValueChange={(value) => setSpeed(Number(value))}
              >
                {SPEEDS.map((rate) => (
                  <DropdownMenuRadioItem key={rate} value={String(rate)} className="tabular-nums">
                    {t("opportunities.scripts.storyboard.animatic.speedOption", "{{rate}}×", {
                      rate,
                    })}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="ghost"
            size="sm"
            iconStart={isExpanded ? "minimize-2" : "maximize-2"}
            className="min-h-11 min-w-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-8 [@media(hover:hover)_and_(pointer:fine)]:min-w-8"
            aria-label={
              isExpanded
                ? t("opportunities.scripts.storyboard.animatic.minimize", "Minimize")
                : t("opportunities.scripts.storyboard.animatic.expand", "Expand")
            }
            onClick={() => setExpanded(!isExpanded)}
          />
        </div>
      </div>
    </div>
  )

  if (!isExpanded) return player

  const modalTitle = title?.trim() || t("opportunities.scripts.storyboard.title")

  // The same player instance moves into the dialog, so playback position, the
  // frame clock and preload state all carry over. A quiet placeholder keeps the
  // card's layout from collapsing behind the modal.
  return (
    <>
      <div
        aria-hidden="true"
        className="mx-auto aspect-[9/16] w-full max-w-xs rounded-lg border border-border/40 bg-muted/50"
      />
      <Dialog open onOpenChange={(open) => !open && setExpanded(false)}>
        <DialogContent
          aria-describedby={undefined}
          className="w-auto max-w-[calc(100vw-2rem)] gap-3 px-4 pb-4 pt-4 sm:max-w-[calc(100vw-2rem)]"
        >
          {/* The modal opens straight from a shared link, so it has to say which
              script this is. The hook is how the script is named everywhere else in
              the list. One line only — a wrapped title pushes the player down and
              reads as body copy — truncated at the player's width, with the full
              hook on the native tooltip. Padded clear of the close button. */}
          <DialogTitle
            title={modalTitle}
            className={cn(
              "mx-auto min-w-0 truncate pr-8 text-left text-sm font-medium leading-snug text-foreground",
              EXPANDED_COLUMN_WIDTH,
            )}
          >
            {modalTitle}
          </DialogTitle>
          {player}
          <StoryboardArtDirectionNote className={cn("mx-auto text-center", EXPANDED_COLUMN_WIDTH)} />
        </DialogContent>
      </Dialog>
    </>
  )
}
